import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmPopup from "@/app/common-hooks/gtm/use-gtm-popup";
import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { QuotePayload } from "@/app/common-types/quote/quote-data";
import { TripDetails } from "@/app/common-types/quote/trip-details";
import { useEditFlowType } from "@/app/common-types/use-edit-quote-review-summary-flow-type";
import selectLocationHookFlash from "@/app/common-utils/common.utils";
import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { Routes } from "@/app/config/page-view-constants";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import {
  CoverEdit,
  DatesEdit,
  QuestionFive,
  QuestionFour,
  QUOTE_JOURNEY_TYPE,
  SAME_DAY,
  SAME_LOCATION,
} from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP } from "@/constants";
import {
  saveQuestionDataBFF,
  sessionCheck,
  updateQuoteOnQuestion,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState, store } from "@/store/store";
import { returnCalculateDate } from "@/utils/travel-date.utils";

import { useTravelDatesContext } from "../providers/travel-dates.provider";
import {
  checkCurentLocation,
  dateDifferenceInDays,
  formatDate,
  stripTimeFromDate,
} from "../utils/date-picker.utils";
import {
  endDateValidationST,
  startDateValidationAMT,
  startDateValidationST,
} from "../utils/date-validations.utils";

export default function useTravelDatesHooks() {
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useDispatch<typeof store.dispatch>();
  const { loading } = quoteResponse;
  const { current } = useWizard();

  const {
    groupedMagnoliaData,
    isModalOpen,
    keyForGroupedMagnolia,
    modalType,
    quotePolicyType,
    // setIsDateError,
    setTravelDates,
    // setTravelDateErrorMessage,
    travelDates,
    maxFromDate,
    maxDateforAMT,
    setError,
    fromLocation,
    toLocation,
    setIsModalOpen,
    setModalType,
  } = useTravelDatesContext();

  const [isFooterLoading, setIsFooterLoading] = useState(false);
  const [isSubmitBtnClicked, setSubmitBtnClicked] = useState<boolean>(false);
  const [payload, setPayload] = useState<QuotePayload | null>(null);

  const datesEditPage = searchParams.get(DatesEdit);
  const coverEditPage = searchParams.get(CoverEdit);
  const editPage = datesEditPage || coverEditPage;
  const { isQuoteReviewEdit: isQuoteReviewCoverEdit } =
    useEditFlowType(coverEditPage);

  const { sdtHeaderText, thcHeaderText } =
    groupedMagnoliaData?.[keyForGroupedMagnolia]?.modalContent ?? {};
  const { errorMessage } = groupedMagnoliaData?.[keyForGroupedMagnolia] ?? {};

  useEffect(() => {
    const startDateOfTrip = quoteResponse?.attributes?.tripDetails?.startDate;
    const endDateOfTrip = quoteResponse?.attributes?.tripDetails?.endDate;

    if (startDateOfTrip && endDateOfTrip && quotePolicyType) {
      const formattedStartDate = new Date(startDateOfTrip); // convert to Date object
      const formattedEndDate = new Date(endDateOfTrip); // convert to Date object
      const baseDate = formattedStartDate ? formattedStartDate : new Date();
      const maxDateST = returnCalculateDate(
        baseDate,
        null,
        null,
        current.brandSpecificParams?.maxDateRange as number
      );

      if (quotePolicyType === ANNUAL_MULTI_TRIP) {
        if (
          !startDateValidationAMT({
            date: formattedStartDate,
            errorMessage,
            maxDateforAMT,
            setError,
          })
        ) {
          return;
        }
        setTravelDates({
          validate:
            stripTimeFromDate(formattedStartDate) >=
            stripTimeFromDate(new Date()),
          startDate: formattedStartDate,
          endDate: formattedStartDate,
        });
      } else {
        if (formattedStartDate === null || formattedEndDate === null) {
          return;
        }
        if (
          !startDateValidationST({
            startDate: formattedStartDate,
            maxFromDate,
            maxDateForReturnST: maxDateST,
            errorMessage,
            setError,
          })
        ) {
          setTravelDates({
            validate: false,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          });
          return;
        }
        // Validate end date
        if (
          !endDateValidationST({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            maxDateForReturnST: maxDateST,
            errorMessage,
            setError,
            setTravelDates,
          })
        ) {
          setTravelDates({
            validate: false,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          });
          return;
        }
        if (
          checkCurentLocation(fromLocation ?? "", toLocation ?? "") &&
          dateDifferenceInDays(formattedStartDate, formattedEndDate) < 2
        ) {
          setModalType(SAME_LOCATION);
          setIsModalOpen(true);
          setTravelDates({
            validate: false,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          });
          return;
        }

        setTravelDates({
          validate:
            stripTimeFromDate(formattedStartDate) >=
            stripTimeFromDate(new Date()),
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quoteResponse?.attributes?.tripDetails?.endDate,
    quoteResponse?.attributes?.tripDetails?.startDate,
  ]);

  const handleSubmitButton = async () => {
    setSubmitBtnClicked(true);

    try {
      setIsFooterLoading(true);

      const formattedStartDate = formatDate(travelDates.startDate as Date);
      const formattedEndDate = formatDate(travelDates.endDate as Date);

      const payloadData: QuotePayload = {
        attributes: {
          ...quoteResponse.attributes,
          tripDetails: {
            ...quoteResponse.attributes.tripDetails,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          },
        },
      };

      setPayload(payloadData);
      dispatch(setQuestionData(payloadData));

      if (!quoteResponse?.referenceId) {
        const newPayload: QuotePayload = {
          type: "IntermediateData",
          channel: "ONLINE",
          attributes: { ...payloadData.attributes },
        };

        const questionData = await dispatch(
          saveQuestionDataBFF({
            payload: { ...newPayload, viewPage: Routes.TravelDates },
            dispatch,
            router,
          })
        );
        if (questionData) {
          router.push(current.next ?? "");
        }
        return;
      }

      if (editPage) {
        const result = await dispatch(
          updateQuoteOnQuestion({
            payload: {
              ...payloadData,
              viewPage: Routes.TravelDates,
              referenceId: quoteResponse.referenceId,
              type: QUOTE_JOURNEY_TYPE,
              channel: "ONLINE",
            },
            router,
            dispatch,
          })
        );
        if (result) {
          // Determine navigation based on edit type and origin
          let nextPage = current?.edges?.editFlowNext;
          if (coverEditPage) {
            // Check if user came from quote-review (sessionStorage is set)
            const editSection = getQuoteReviewSummaryEditSection();
            if (editSection === "cover") {
              // User came from quote-review, use new flow
              nextPage = current?.edges?.coverEditQuoteSummaryNext; // → /travelling-to/?coverEdit=true
            } else {
              // User came from quote page, use old flow
              nextPage = current?.edges?.editFlowNext; // → /quote
            }
          }

          router.push(nextPage ?? "");
        }
        return;
      }

      dispatch(sessionCheck({ dispatch, router }));
      const path =
        editPage && current.edges?.editFlowNext
          ? current.edges?.editFlowNext
          : current.next ?? "";
      router.push(path);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error in handleSubmit:", error);
      setIsFooterLoading(false);
    }
  };

  const handleBackButton = () => {
    if (editPage) {
      // Check if user came from quote-review (sessionStorage is set)
      const editSection = getQuoteReviewSummaryEditSection();
      if (editSection === "cover") {
        // User came from quote-review, navigate to quote-review
        router.push(current.edges.coverEditQuoteSummaryPrev ?? "");
      } else {
        // User came from quote page, use old flow
        router.push(current.edges.coverEditPrev ?? "");
      }
    } else {
      router.push(current.prev ?? "");
    }
  };

  const toLocationData = selectLocationHookFlash(
    payload?.attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionFive,
    tripType: quotePolicyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    coverStartDate: payload?.attributes?.tripDetails?.startDate || undefined,
    coverEndDate: payload?.attributes?.tripDetails?.endDate || undefined,
    cruise: payload?.attributes?.tripDetails?.goingOnCruise ?? undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation || undefined,
    isSubmitBtnClicked,
  });

  useGtmPopup({
    tripType: QuestionFive,
    isPopupOpen: isModalOpen,
    popupName: modalType === SAME_DAY ? sdtHeaderText : thcHeaderText,
  });

  return {
    isFooterLoading,
    loading,
    editPage,
    datesEditPage,
    coverEditPage,
    isQuoteReviewCoverEdit,
    handleSubmitButton,
    handleBackButton,
  };
}
